import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  listSecrets,
  createSecret,
  deleteSecret,
  getSecretDeleteConfirm,
  getGitHubOAuthURL,
  SECRET_TYPE_APP_STORE,
  SECRET_TYPE_APP_STORE_IAP,
  SECRET_TYPE_PLAY_STORE,
  SECRET_TYPE_GITHUB,
  SECRET_TYPE_REVENUECAT,
  SECRET_TYPE_APPLE_SEARCH_ADS,
  type Secret,
  type CreateSecretInput,
} from '../api/client';
import { Info, Key, Plus, Trash2 } from 'lucide-react';
import { useError } from '../context/ErrorContext';
import ConfirmDialog from './ConfirmDialog';
import ModalLayout from './ModalLayout';
import { SkeletonTableRow } from './Skeleton';

const SECRET_TYPES: { value: string; labelKey: string }[] = [
  { value: SECRET_TYPE_APP_STORE, labelKey: 'panel.appStoreSecret' },
  { value: SECRET_TYPE_APP_STORE_IAP, labelKey: 'panel.appStoreIapSecret' },
  { value: SECRET_TYPE_PLAY_STORE, labelKey: 'panel.playStoreSecret' },
  { value: SECRET_TYPE_GITHUB, labelKey: 'panel.githubSecret' },
];

type ModalStep = 'type' | 'form';

const Secrets: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [secrets, setSecrets] = useState<Secret[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<ModalStep>('type');
  const [form, setForm] = useState<CreateSecretInput>({
    type: SECRET_TYPE_APP_STORE,
    name: '',
    payload: {},
  });
  const [appStoreKeyId, setAppStoreKeyId] = useState('');
  const [appStoreIssuerId, setAppStoreIssuerId] = useState('');
  const [appStoreKeyContent, setAppStoreKeyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [githubConnecting, setGithubConnecting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState<string | null>(null);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { showError: showErrorPopup } = useError();

  const openModal = () => {
    setModalStep('type');
    setForm({ type: SECRET_TYPE_APP_STORE, name: '', payload: {} });
    setAppStoreKeyId('');
    setAppStoreIssuerId('');
    setAppStoreKeyContent('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalStep('type');
  };

  const selectType = (type: string) => {
    setForm(f => ({ ...f, type, payload: {} }));
    setAppStoreKeyId('');
    setAppStoreIssuerId('');
    setAppStoreKeyContent('');
    setModalStep('form');
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSecrets();
      setSecrets(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.loadFailed');
      setError(msg);
      showErrorPopup(msg);
    } finally {
      setLoading(false);
    }
  }, [showErrorPopup, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const connected = searchParams.get('github_connected');
    const ghError = searchParams.get('github_error');
    if (connected === '1') {
      setSearchParams({}, { replace: true });
      load();
    } else if (ghError) {
      setSearchParams({}, { replace: true });
      showErrorPopup(t('panel.githubOAuthError', { code: ghError }));
    }
  }, [searchParams, setSearchParams, load, showErrorPopup, t]);

  const handleGitHubConnect = async () => {
    setGithubConnecting(true);
    try {
      const url = await getGitHubOAuthURL();
      window.location.href = url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.loadFailed');
      showErrorPopup(msg);
      setGithubConnecting(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (form.type === SECRET_TYPE_GITHUB) {
      await handleGitHubConnect();
      return;
    }
    if (!form.name.trim()) return;
    const payload =
      form.type === SECRET_TYPE_APP_STORE || form.type === SECRET_TYPE_APP_STORE_IAP
        ? {
            key_id: appStoreKeyId.trim() || undefined,
            issuer_id: appStoreIssuerId.trim() || undefined,
            key: appStoreKeyContent.trim() || undefined,
          }
        : {};
    setSubmitting(true);
    try {
      await createSecret({ ...form, payload });
      closeModal();
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.createFailed');
      setError(msg);
      showErrorPopup(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteConfirmId(id);
    setDeleteConfirmMessage(null);
    setDeleteConfirmLoading(true);
    try {
      const res = await getSecretDeleteConfirm(id);
      setDeleteConfirmMessage(res.message);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.loadFailed');
      showErrorPopup(msg);
      setDeleteConfirmId(null);
    } finally {
      setDeleteConfirmLoading(false);
    }
  };

  const closeDeleteConfirm = () => {
    if (deleting || deleteConfirmLoading) return;
    setDeleteConfirmId(null);
    setDeleteConfirmMessage(null);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId || deleteConfirmLoading) return;
    setDeleting(true);
    try {
      await deleteSecret(deleteConfirmId);
      setDeleteConfirmId(null);
      setDeleteConfirmMessage(null);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.deleteFailed');
      showErrorPopup(msg);
    } finally {
      setDeleting(false);
    }
  };

  const secretTypeLabel = (type: string) => {
    if (type === SECRET_TYPE_APP_STORE) return t('panel.appStore');
    if (type === SECRET_TYPE_APP_STORE_IAP) return t('panel.appStoreIap');
    if (type === SECRET_TYPE_PLAY_STORE) return t('panel.playStore');
    if (type === SECRET_TYPE_GITHUB) return t('panel.github');
    if (type === SECRET_TYPE_REVENUECAT) return t('panel.revenueCat');
    if (type === SECRET_TYPE_APPLE_SEARCH_ADS) return t('panel.appleSearchAds');
    return type;
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('panel.secretsTitle')}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          {t('panel.secretsSubtitle')}
        </p>
      </div>

      <div className="rounded-xl border border-primary-200 dark:border-primary-800/60 bg-primary-50/40 dark:bg-primary-900/10 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Info className="h-4 w-4 text-primary-600 dark:text-primary-400 shrink-0" />
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('onboarding.secrets.infoTitle')}</h3>
        </div>
        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li><span className="font-medium text-gray-700 dark:text-gray-300">App Store Connect</span> — {t('onboarding.secrets.infoAppStore')}</li>
          <li><span className="font-medium text-gray-700 dark:text-gray-300">App Store Connect (IAP)</span> — {t('onboarding.secrets.infoAppStoreIap')}</li>
          <li><span className="font-medium text-gray-700 dark:text-gray-300">Play Store</span> — {t('onboarding.secrets.infoPlayStore')}</li>
          <li><span className="font-medium text-gray-700 dark:text-gray-300">GitHub</span> — {t('onboarding.secrets.infoGitHub')}</li>
        </ul>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('panel.secretList')}
          </h2>
          <button
            type="button"
            onClick={openModal}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            {t('panel.newSecret')}
          </button>
        </div>

        {loading ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-4 font-semibold">{t('panel.type')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('panel.secretName')}</th>
                  <th className="py-2 pr-4 font-semibold w-20">{t('panel.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonTableRow key={i} cols={3} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-4 font-semibold">{t('panel.type')}</th>
                  <th className="py-2 pr-4 font-semibold">{t('panel.secretName')}</th>
                  <th className="py-2 pr-4 font-semibold w-20">{t('panel.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {secrets.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-gray-500 dark:text-gray-400">
                      {t('panel.noSecrets')}
                    </td>
                  </tr>
                ) : (
                  secrets.map(s => (
                    <tr
                      key={s.id}
                      className="border-b border-gray-100 dark:border-gray-700/50"
                    >
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          <Key className="h-3.5 w-3.5" />
                          {secretTypeLabel(s.type)}
                        </span>
                      </td>
                      <td className="py-3 pr-4 font-medium">{s.name}</td>
                      <td className="py-3 pr-4">
                        <button
                          type="button"
                          onClick={() => void handleDelete(s.id)}
                          className="p-1.5 rounded text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModalLayout
        open={modalOpen}
        onClose={closeModal}
        title={modalStep === 'type' ? t('panel.selectSecretType') : t('panel.newSecret')}
        icon={<Key className="h-5 w-5 text-primary-600 dark:text-primary-400" />}
        description={modalStep === 'type' ? t('panel.selectSecretTypeHint') : undefined}
        primaryLabel={
          modalStep === 'type'
            ? ''
            : form.type === SECRET_TYPE_GITHUB
              ? (githubConnecting ? t('panel.connecting') : t('panel.continueWithGitHub'))
              : (submitting ? t('panel.saving') : t('panel.add'))
        }
        onPrimary={
          modalStep === 'type'
            ? closeModal
            : form.type === SECRET_TYPE_GITHUB
              ? () => void handleGitHubConnect()
              : () => void handleCreate({ preventDefault: () => {}, stopPropagation: () => {} } as React.FormEvent)
        }
        primaryDisabled={
          modalStep === 'type'
            ? false
            : form.type === SECRET_TYPE_GITHUB
              ? githubConnecting
              : submitting ||
                !form.name.trim() ||
                ((form.type === SECRET_TYPE_APP_STORE || form.type === SECRET_TYPE_APP_STORE_IAP) &&
                  (!appStoreKeyId.trim() || !appStoreIssuerId.trim() || !appStoreKeyContent.trim()))
        }
        primaryLoading={modalStep !== 'type' && (submitting || githubConnecting)}
      >
        {modalStep === 'type' ? (
          <div className="space-y-2">
            {SECRET_TYPES.map(typ => (
              <button
                key={typ.value}
                type="button"
                onClick={() => selectType(typ.value)}
                className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/50 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition-all text-left"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-primary-100 dark:bg-primary-900/30">
                  <Key className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <span className="font-medium text-gray-900 dark:text-white">
                  {t(typ.labelKey)}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <button
                type="button"
                onClick={() => setModalStep('type')}
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                ← {t('panel.changeType')}
              </button>
              <span className="text-gray-400 dark:text-gray-500">
                {(() => {
                  const typ = SECRET_TYPES.find(ty => ty.value === form.type);
                  return typ ? t(typ.labelKey) : null;
                })()}
              </span>
            </div>
            {form.type === SECRET_TYPE_GITHUB ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('panel.githubOAuthHint')}
              </p>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('panel.secretName')}
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('panel.secretNamePlaceholder')}
                    required
                  />
                </div>
                {form.type === SECRET_TYPE_APP_STORE && (
                  <>
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-0.5">
                        {t('panel.appStoreKeyNote')}
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {t('panel.appStoreKeyNoteDetail')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('panel.keyId')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={appStoreKeyId}
                        onChange={e => setAppStoreKeyId(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                        placeholder="XXXXXXXXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('panel.issuerId')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={appStoreIssuerId}
                        onChange={e => setAppStoreIssuerId(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                        placeholder={t('panel.issuerIdPlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('panel.p8Content')} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={appStoreKeyContent}
                        onChange={e => setAppStoreKeyContent(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
                        placeholder={t('panel.p8Placeholder')}
                        spellCheck={false}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('panel.p8Help')}
                      </p>
                    </div>
                  </>
                )}
                {form.type === SECRET_TYPE_APP_STORE_IAP && (
                  <>
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-0.5">
                        {t('panel.appStoreIapKeyNote')}
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {t('panel.appStoreIapKeyNoteDetail')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('panel.keyId')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={appStoreKeyId}
                        onChange={e => setAppStoreKeyId(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                        placeholder="XXXXXXXXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('panel.issuerId')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={appStoreIssuerId}
                        onChange={e => setAppStoreIssuerId(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                        placeholder={t('panel.issuerIdIapPlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('panel.p8Content')} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={appStoreKeyContent}
                        onChange={e => setAppStoreKeyContent(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
                        placeholder={t('panel.p8Placeholder')}
                        spellCheck={false}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('panel.p8Help')}
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </ModalLayout>
      <ConfirmDialog
        open={Boolean(deleteConfirmId)}
        onClose={closeDeleteConfirm}
        title={t('panel.deleteSecretTitle')}
        message={
          deleteConfirmLoading || !deleteConfirmMessage
            ? t('common.loading')
            : deleteConfirmMessage
        }
        confirmLabel={deleting ? t('panel.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDelete()}
        confirmLoading={deleting || deleteConfirmLoading}
        closeDisabled={deleting || deleteConfirmLoading}
        variant="danger"
      />
    </div>
  );
};

export default Secrets;
